import { useAccountUsers, useCancelSubscription } from '@my/directory-api/hooks';
import type { SupportSubscription } from '@my/directory-api';
import { ConfirmDialog, FormSection, showToast } from '@my/ui';
import { Ban } from 'lucide-react';
import { useState } from 'react';
import { EnableAppPanel } from './EnableAppPanel';
import { appPillClass } from './lib/appColours';
import { useAccountContext } from './useAccountContext';

/** What this account is entitled to, and how to add to it. */
export function AccountSubscriptionsPage() {
  const { account, isReady } = useAccountContext();
  const cancel = useCancelSubscription();
  const users = useAccountUsers({ enabled: isReady });
  const [pending, setPending] = useState<SupportSubscription | null>(null);

  // How many people actually lose something. A warning that says "users will
  // lose access" is ignorable; one that says "9 people lose access to Depot
  // immediately" is a decision.
  const affected = pending
    ? (users.data ?? []).filter((u) =>
        u.accounts
          ?.find((a) => a.id === account.id)
          ?.appRoles.some((r) => r.app === pending.appKey),
      ).length
    : 0;

  const confirmCancel = () => {
    if (!pending) return;
    const app = pending.appKey;
    cancel.mutate(pending.id, {
      onSuccess: () => {
        showToast({ type: 'success', message: `${app} cancelled for ${account.name}.` });
        setPending(null);
      },
      onError: (err) => showToast({ type: 'error', message: err.message }),
    });
  };

  return (
    <>
      <FormSection title="Subscriptions" columns={1}>
        {account.subscriptions.length === 0 ? (
          <p className="text-xs italic text-slate-400">
            No subscriptions — nothing in the suite is reachable for this account.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-md">
            <table className="w-full table-auto text-xs">
              <thead className="bg-slate-700 text-[11px] uppercase tracking-wide text-white">
                <tr>
                  <th className="whitespace-nowrap px-3 py-2 text-left font-semibold">App</th>
                  <th className="whitespace-nowrap px-3 py-2 text-left font-semibold">Plan</th>
                  <th className="whitespace-nowrap px-3 py-2 text-left font-semibold">Starts</th>
                  <th className="whitespace-nowrap px-3 py-2 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {account.subscriptions.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50">
                    <td className="whitespace-nowrap px-3 py-1 text-xs font-semibold text-slate-900">
                      <span className="inline-flex items-center gap-2">
                        <span className={appPillClass(s.appKey)}>{s.appKey}</span>
                        {/*
                          Only when there is something wrong to say. A "Live"
                          badge on every healthy row would be noise on the
                          normal case and make the abnormal one harder to spot,
                          so the absence of a badge IS the healthy state.
                        */}
                        {!s.isActive && (
                          <span className="inline-flex items-center rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-medium text-rose-700">
                            {statusOf(s)}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-1 text-xs">
                      {/* Neutral on purpose: the colour in this row means
                          "which app", and a second coloured pill beside it
                          would compete with that rather than add to it. */}
                      <span className="inline-block rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                        {s.plan}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-1 text-xs text-slate-500">
                      {formatDate(s.startsUtc)}
                      {s.expiresUtc && (
                        // Carried on the Starts cell rather than given a column
                        // of its own: most subscriptions never expire, so a
                        // fourth column would be empty on nearly every row.
                        <span className="ml-2 text-slate-400">
                          → {formatDate(s.expiresUtc)}
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1 text-right text-xs">
                      {/* Only on a live subscription: cancelling an expired one
                          changes nothing and the button would only invite the
                          question of what it did. */}
                      {s.isActive && (
                        <button
                          type="button"
                          aria-label={`Cancel ${s.appKey}`}
                          onClick={() => setPending(s)}
                          className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-rose-600 hover:bg-rose-50"
                        >
                          <Ban className="h-3.5 w-3.5" aria-hidden />
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </FormSection>

      <FormSection title="Enable an app" columns={1}>
        {isReady ? (
          // Waits for the account header: provisioning writes to whichever
          // account X-Account-Key names, and firing it a render early would aim
          // it at nothing.
          <EnableAppPanel account={account} />
        ) : (
          <p className="text-xs italic text-slate-400">Loading…</p>
        )}
      </FormSection>

      <ConfirmDialog
        open={pending !== null}
        tone="danger"
        title={`Cancel ${pending?.appKey ?? ''} for ${account.name}?`}
        confirmLabel="Cancel subscription"
        cancelLabel="Keep it"
        busy={cancel.isPending}
        onCancel={() => setPending(null)}
        onConfirm={confirmCancel}
        message={
          <div className="space-y-2 text-sm">
            <p>
              <strong>
                {affected === 0
                  ? 'Nobody currently has access to this app.'
                  : `${affected} ${affected === 1 ? 'person loses' : 'people lose'} access immediately.`}
              </strong>{' '}
              They will be signed out of {pending?.appKey} the next time their session refreshes.
            </p>
            <p className="text-slate-600">
              Their access is remembered. Enabling {pending?.appKey} again restores exactly who had
              what — nothing is deleted, and no data in the app is touched.
            </p>
            <p className="text-slate-600">
              Billing is not affected by this action.
            </p>
          </div>
        }
      />
    </>
  );
}

/**
 * Why a subscription is not live. Expiry is checked first: an expired row is
 * usually also still `enabled`, and "Expired" is the answer somebody needs —
 * "Disabled" would send them looking for a switch nobody threw.
 */
function statusOf(s: SupportSubscription): string {
  if (s.expiresUtc && new Date(s.expiresUtc) <= new Date()) return 'Expired';
  if (!s.enabled) return 'Disabled';
  if (new Date(s.startsUtc) > new Date()) return 'Not started';
  return 'Inactive';
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}
